import { useCallback } from 'react';
import { toast } from 'sonner';
import { saveTransmission } from '@/services/transmissionsService';
import { fetchVerifiedCovers, coverCacheKey } from '@/services/verifiedCovers';

/**
 * Adds a previewed book to the signed-in user's transmissions.
 *
 * EnhancedBookPreviewModal renders its "Add Signal" button unconditionally, but
 * six of its call sites passed `onAddBook={() => {}}`. On those surfaces the
 * button was live, clickable and completely inert: no row written, no toast, no
 * console error. Adding a book from a followed reader's library on /community
 * simply did nothing. Centralising the handler means a call site can no longer
 * render the button while quietly dropping the click.
 *
 * The behaviour mirrors ReadingNarrative's implementation, which was the only
 * self-contained version that actually worked.
 *
 * iOS SAFETY: this never sets `protagonist`, and the native app only reads
 * transmissions rows where `protagonist` is not null, so rows added here are
 * invisible to it. Do not add a protagonist field to this path.
 */

/** The subset of a previewed book this needs. Structural, so the various
 *  preview shapes (EnrichedPublisherBook and friends) all satisfy it. */
export interface AddableSignal {
  title: string;
  author?: string;
  cover_url?: string;
  google_cover_url?: string;
  isbn?: string;
}

interface UseAddSignalOptions {
  /** Where the add came from, for the toast copy only. */
  label?: string;
  /** Run after a successful add — refresh a list, close a panel. */
  onAdded?: (book: AddableSignal) => void;
}

export function useAddSignal(options: UseAddSignalOptions = {}) {
  const { onAdded } = options;

  return useCallback(
    async (book: AddableSignal): Promise<boolean> => {
      if (!book?.title) {
        toast.error("Couldn't add this — it has no title");
        return false;
      }

      try {
        // A book added from a link (the newsletter, a shared URL) arrives with
        // no cover, and sat coverless on the shelf until the 6-hourly job. Ask
        // the verified rule now; it is cached, so this is usually instant.
        let cover = book.cover_url || book.google_cover_url || '';
        if (!cover && book.author) {
          try {
            const found = await fetchVerifiedCovers([{ title: book.title, author: book.author }]);
            cover = found.get(coverCacheKey(book.title, book.author)) ?? '';
          } catch { /* no cover yet; the 6-hourly job fills it */ }
        }
        await saveTransmission({
          title: book.title,
          author: book.author || '',
          status: 'want-to-read',
          tags: [],
          notes: '',
          cover_url: cover,
          rating: {},
          // Carried through when the preview has it: saveTransmission dedupes on
          // title+author, but a stored ISBN is what later enrichment (covers,
          // Apple links) keys off, so dropping it here would quietly produce a
          // thinner row than the main add path creates.
          ...(book.isbn ? { isbn: book.isbn } : {}),
        });
        toast.success(`Added "${book.title}" to your signals`);
        onAdded?.(book);
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : '';
        if (message.startsWith('DUPLICATE:')) {
          toast.info(`"${book.title}" is already in your signals`);
        } else if (message.includes('not authenticated')) {
          toast.error('Sign in to add this to your signals');
        } else {
          toast.error(`Couldn't add "${book.title}"`);
        }
        return false;
      }
    },
    [onAdded],
  );
}
